import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import { ZoomableImage } from "components/common/ZoomableImage";
import { cn } from "lib/utils";

function isVideoItem(item) {
  if (item?.type) {
    return item.type === "video";
  }
  const src = item?.src || "";
  return /\.(mp4|webm|ogg|mov)$/i.test(src);
}

function normalizeItems(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item, index) => {
      if (typeof item === "string") {
        return {
          id: `${item}-${index}`,
          src: item,
          alt: `slide-${index + 1}`,
          type: /\.(mp4|webm|ogg|mov)$/i.test(item) ? "video" : "image",
        };
      }
      return {
        id: item.id || `${item.src}-${index}`,
        src: item.src,
        thumbnail: item.thumbnail || item.poster || item.src,
        poster: item.poster,
        alt: item.alt || `slide-${index + 1}`,
        type: item.type || (isVideoItem(item) ? "video" : "image"),
      };
    })
    .filter((item) => Boolean(item.src));
}

function VideoThumbFallback() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
      <Play className="h-4 w-4" aria-hidden />
    </div>
  );
}

/**
 * Image / video carousel with prev/next, optional thumbnails, optional autoplay.
 * `items` may be URL strings or `{ src, alt?, type?, thumbnail?, poster?, id? }`.
 */
export default function AppCarousel({
  items,
  initialIndex = 0,
  onSlideChange,
  autoPlay = false,
  autoPlayInterval = 4000,
  className,
  heightClassName = "h-[280px] md:h-[380px] lg:h-[420px]",
}) {
  const mediaItems = useMemo(() => normalizeItems(items), [items]);
  const safeInitialIndex = Math.min(Math.max(initialIndex, 0), Math.max(mediaItems.length - 1, 0));
  const [activeIndex, setActiveIndex] = useState(safeInitialIndex);

  useEffect(() => {
    setActiveIndex(Math.min(Math.max(initialIndex, 0), Math.max(mediaItems.length - 1, 0)));
  }, [initialIndex, mediaItems.length]);

  const setSlide = useCallback(
    (nextIndex) => {
      if (!mediaItems.length) return;
      const wrappedIndex = (nextIndex + mediaItems.length) % mediaItems.length;
      setActiveIndex(wrappedIndex);
      onSlideChange?.(wrappedIndex, mediaItems[wrappedIndex]);
    },
    [mediaItems, onSlideChange],
  );

  const hasMultiple = mediaItems.length > 1;

  useEffect(() => {
    if (!autoPlay || !hasMultiple) return undefined;
    const timer = window.setInterval(() => {
      setSlide(activeIndex + 1);
    }, autoPlayInterval);
    return () => window.clearInterval(timer);
  }, [autoPlay, hasMultiple, activeIndex, autoPlayInterval, setSlide]);

  if (!mediaItems.length) {
    return (
      <div
        className={cn(
          "rounded-xl border border-border bg-muted p-8 text-center text-sm text-muted-foreground",
          className,
        )}
      >
        No media available.
      </div>
    );
  }

  const active = mediaItems[activeIndex];

  return (
    <section
      className={cn(
        "relative overflow-hidden rounded-xl border border-border bg-muted shadow-sm",
        className,
      )}
    >
      <div className={cn("relative w-full", heightClassName)}>
        {active.type === "video" ? (
          <video
            key={active.src}
            className="h-full w-full object-cover"
            controls
            preload="metadata"
            poster={active.poster || active.thumbnail}
          >
            <source src={active.src} />
            Your browser does not support the video tag.
          </video>
        ) : (
          <ZoomableImage
            src={active.src}
            alt={active.alt}
            className="object-cover"
            containerClassName="h-full w-full"
          />
        )}

        {hasMultiple ? (
          <>
            <button
              type="button"
              onClick={() => setSlide(activeIndex - 1)}
              className="absolute left-3 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground/70 text-background backdrop-blur-sm transition hover:bg-foreground/85 md:left-4 md:h-11 md:w-11"
              aria-label="Previous slide"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => setSlide(activeIndex + 1)}
              className="absolute right-3 top-1/2 inline-flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-foreground/70 text-background backdrop-blur-sm transition hover:bg-foreground/85 md:right-4 md:h-11 md:w-11"
              aria-label="Next slide"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        ) : null}
      </div>

      {hasMultiple ? (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/55 via-black/20 to-transparent px-3 pb-3 pt-10 md:px-4 md:pb-4 md:pt-12">
          <div className="mx-auto flex max-w-3xl items-center justify-center gap-2 overflow-x-auto md:gap-3">
            {mediaItems.map((item, index) => {
              const isActive = index === activeIndex;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => setSlide(index)}
                  className={cn(
                    "relative h-14 w-24 shrink-0 overflow-hidden rounded-lg border transition md:h-16 md:w-28 md:rounded-xl",
                    isActive
                      ? "border-accent ring-2 ring-accent/50"
                      : "border-white/40 hover:border-white/70",
                  )}
                  aria-label={`Open slide ${index + 1}`}
                  aria-current={isActive ? "true" : "false"}
                >
                  {item.type === "video" ? (
                    <>
                      {item.thumbnail ? (
                        <ZoomableImage
                          src={item.thumbnail}
                          alt=""
                          variant="lightbox"
                          className="object-cover"
                          containerClassName="h-full w-full"
                        />
                      ) : (
                        <VideoThumbFallback />
                      )}
                      <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/25 text-white">
                        <Play className="h-4 w-4" />
                      </span>
                    </>
                  ) : (
                    <ZoomableImage
                      src={item.thumbnail || item.src}
                      alt=""
                      variant="lightbox"
                      className="object-cover"
                      containerClassName="h-full w-full"
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
}
